import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, Upload, Image as ImageIcon, Star,
  Type, Layers, Tag, MessageSquare, HelpCircle, ShieldCheck, Settings as SettingsIcon, AlertCircle,
} from "lucide-react";
import SiteHeader from "../components/SiteHeader";
import { api, fileUrl, BACKEND_URL } from "../lib/api";
import { useCms } from "../lib/cms";
import { toast } from "sonner";

const TABS = [
  { id: "inhalte", label: "Inhalte", icon: Type },
  { id: "bilder", label: "Bilder", icon: ImageIcon },
  { id: "preise", label: "Preise", icon: Tag },
  { id: "bewertungen", label: "Bewertungen", icon: Star },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "rechtliches", label: "Rechtliches", icon: ShieldCheck },
  { id: "einstellungen", label: "Einstellungen", icon: SettingsIcon },
];

function clone(obj) {
  return JSON.parse(JSON.stringify(obj || {}));
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/uploads", fd, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return `${BACKEND_URL}${data.url}`;
}

export default function AdminCMS() {
  const navigate = useNavigate();
  const { cms, refresh } = useCms();
  const [tab, setTab] = useState("inhalte");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin/login");
      return;
    }
    refresh();
  }, [navigate]); // eslint-disable-line

  useEffect(() => {
    setDraft(clone(cms));
  }, [cms]);

  if (!draft) {
    return (
      <div className="min-h-screen bg-slate-50">
        <SiteHeader />
        <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>
      </div>
    );
  }

  const saveSection = async (sectionKeys) => {
    setSaving(true);
    try {
      const payload = {};
      sectionKeys.forEach((k) => { payload[k] = draft[k]; });
      await api.put("/admin/cms", payload);
      await refresh();
      toast.success("Gespeichert");
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Speichern fehlgeschlagen");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <SiteHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-brand-amberDark">CMS</div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">Website bearbeiten</h1>
            <p className="text-sm text-slate-600 mt-1">Texte, Bilder, Preise und mehr — ohne Code-Änderung.</p>
          </div>
          <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-white transition-colors" data-testid="cms-back">
            <ArrowLeft size={16} /> Zurück
          </button>
        </div>

        {/* Tabs */}
        <div className="overflow-x-auto no-scrollbar mb-4">
          <div className="inline-flex bg-white border border-slate-200 rounded-2xl p-1 shadow-soft" data-testid="cms-tabs">
            {TABS.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap transition-colors ${
                  tab === t.id ? "bg-brand-navy text-white" : "text-slate-600 hover:text-slate-900"
                }`}
                data-testid={`cms-tab-${t.id}`}
              >
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "inhalte" && <InhalteTab draft={draft} setDraft={setDraft} onSave={() => saveSection(["hero", "cards", "trust", "prices_note"])} saving={saving} />}
        {tab === "bilder" && <BilderTab draft={draft} setDraft={setDraft} onSave={() => saveSection(["images"])} saving={saving} />}
        {tab === "preise" && <PreiseTab draft={draft} setDraft={setDraft} onSave={() => saveSection(["prices", "prices_tiers", "prices_note"])} saving={saving} />}
        {tab === "bewertungen" && <BewertungenTab draft={draft} setDraft={setDraft} onSave={() => saveSection(["reviews"])} saving={saving} />}
        {tab === "faq" && <FAQTab draft={draft} setDraft={setDraft} onSave={() => saveSection(["faqs"])} saving={saving} />}
        {tab === "rechtliches" && <RechtlichesTab draft={draft} setDraft={setDraft} onSave={() => saveSection(["legal"])} saving={saving} />}
        {tab === "einstellungen" && <EinstellungenTab />}
      </div>
    </div>
  );
}

const fieldClass = "w-full rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm outline-none focus:border-brand-amber focus:ring-4 focus:ring-amber-100 transition-all bg-white";

function Card({ title, children, action }) {
  return (
    <div className="rounded-3xl bg-white border border-slate-200 shadow-soft p-5 sm:p-6 mb-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-bold text-slate-900 text-lg">{title}</h2>
        {action}
      </div>
      {children}
    </div>
  );
}

function FieldRow({ label, children, hint }) {
  return (
    <div className="mb-3">
      <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</label>
      {children}
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

function SaveBar({ onSave, saving }) {
  return (
    <div className="sticky bottom-4 z-30 mt-6 flex justify-end">
      <button
        onClick={onSave}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-2xl bg-brand-navy text-white px-5 py-3 text-sm font-semibold shadow-floating hover:bg-slate-800 disabled:opacity-60 transition-colors"
        data-testid="cms-save"
      >
        {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Speichern
      </button>
    </div>
  );
}

function InhalteTab({ draft, setDraft, onSave, saving }) {
  const hero = draft.hero || {};
  const cards = draft.cards || {};
  const trust = draft.trust || {};
  return (
    <div>
      <Card title="Hero / Startseite">
        <FieldRow label="Hauptüberschrift">
          <textarea rows={2} className={fieldClass} value={hero.headline || ""} onChange={(e) => setDraft({ ...draft, hero: { ...hero, headline: e.target.value } })} data-testid="cms-hero-headline" />
        </FieldRow>
        <FieldRow label="Hervorgehobener Teil">
          <input className={fieldClass} value={hero.highlight || ""} onChange={(e) => setDraft({ ...draft, hero: { ...hero, highlight: e.target.value } })} />
        </FieldRow>
        <FieldRow label="Unterüberschrift">
          <textarea rows={2} className={fieldClass} value={hero.subheadline || ""} onChange={(e) => setDraft({ ...draft, hero: { ...hero, subheadline: e.target.value } })} data-testid="cms-hero-subheadline" />
        </FieldRow>
        <FieldRow label="Top-Badge">
          <input className={fieldClass} value={hero.badge || ""} onChange={(e) => setDraft({ ...draft, hero: { ...hero, badge: e.target.value } })} />
        </FieldRow>
        <FieldRow label="Service-Region (Text unter Hero / Footer-Hinweis)">
          <textarea rows={2} className={fieldClass} value={hero.service_area || ""} onChange={(e) => setDraft({ ...draft, hero: { ...hero, service_area: e.target.value } })} />
        </FieldRow>
      </Card>

      <Card title="Service-Karten">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {["small", "full"].map((key) => {
            const c = cards[key] || {};
            const title = key === "small" ? "Kleine Transporte" : "Kompletter Umzug";
            return (
              <div key={key} className="rounded-2xl border border-slate-200 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">{title}</div>
                <FieldRow label="Titel"><input className={fieldClass} value={c.title || ""} onChange={(e) => setDraft({ ...draft, cards: { ...cards, [key]: { ...c, title: e.target.value } } })} /></FieldRow>
                <FieldRow label="Beschreibung"><textarea rows={2} className={fieldClass} value={c.description || ""} onChange={(e) => setDraft({ ...draft, cards: { ...cards, [key]: { ...c, description: e.target.value } } })} /></FieldRow>
                <FieldRow label="Beispiele (eine pro Zeile)">
                  <textarea rows={4} className={fieldClass} value={(c.examples || []).join("\n")} onChange={(e) => setDraft({ ...draft, cards: { ...cards, [key]: { ...c, examples: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) } } })} />
                </FieldRow>
                <FieldRow label="Button-Text"><input className={fieldClass} value={c.cta || ""} onChange={(e) => setDraft({ ...draft, cards: { ...cards, [key]: { ...c, cta: e.target.value } } })} /></FieldRow>
              </div>
            );
          })}
        </div>
      </Card>

      <Card title="Trust-Badges">
        {[
          ["rating_label", "Sterne / Bewertung"],
          ["count_label", "Anzahl Transporte"],
          ["whatsapp_label", "WhatsApp-Hinweis"],
          ["safety_label", "Sicherheits-Hinweis"],
        ].map(([k, label]) => (
          <FieldRow key={k} label={label}>
            <input className={fieldClass} value={trust[k] || ""} onChange={(e) => setDraft({ ...draft, trust: { ...trust, [k]: e.target.value } })} />
          </FieldRow>
        ))}
      </Card>

      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

function BilderTab({ draft, setDraft, onSave, saving }) {
  const images = draft.images || {};
  const strip = images.hero_strip || [];

  const updateStrip = (idx, val) => {
    const next = [...strip];
    next[idx] = val;
    setDraft({ ...draft, images: { ...images, hero_strip: next } });
  };

  const onUpload = async (idx, file) => {
    try {
      const url = await uploadImage(file);
      updateStrip(idx, url);
      toast.success("Bild hochgeladen");
    } catch {
      toast.error("Upload fehlgeschlagen");
    }
  };

  const ensureSlots = () => {
    while (strip.length < 3) strip.push("");
  };
  ensureSlots();

  return (
    <div>
      <Card title="Hero-Bildstreifen (3 Bilder)">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((idx) => (
            <div key={idx} className="rounded-2xl border border-slate-200 p-3">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Bild {idx + 1}</div>
              <div className="aspect-[5/3] rounded-xl bg-slate-100 overflow-hidden mb-2 border border-slate-200">
                {strip[idx] ? <img src={strip[idx]} alt="" className="w-full h-full object-cover" /> : (
                  <div className="w-full h-full grid place-items-center text-slate-400 text-xs">Kein Bild</div>
                )}
              </div>
              <FieldRow label="URL oder Upload">
                <input className={fieldClass + " mb-2"} value={strip[idx] || ""} onChange={(e) => updateStrip(idx, e.target.value)} placeholder="https://..." />
              </FieldRow>
              <label className="inline-flex items-center gap-1.5 cursor-pointer rounded-xl border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-900 px-3 py-2 text-xs font-semibold transition-colors">
                <Upload size={12} /> Hochladen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(idx, e.target.files[0])} data-testid={`cms-upload-hero-${idx}`} />
              </label>
            </div>
          ))}
        </div>
      </Card>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

function PreiseTab({ draft, setDraft, onSave, saving }) {
  const prices = draft.prices || [];
  const tiers = draft.prices_tiers || {};

  const updateCard = (i, patch) => {
    const next = prices.map((p, idx) => (idx === i ? { ...p, ...patch } : p));
    setDraft({ ...draft, prices: next });
  };
  const removeCard = (i) => setDraft({ ...draft, prices: prices.filter((_, idx) => idx !== i) });
  const addCard = () => setDraft({ ...draft, prices: [...prices, { tag: "Neu", title: "", range: "", text: "" }] });

  const setTier = (typeKey, tierKey, idx, val) => {
    const t = clone(tiers);
    t[typeKey] = t[typeKey] || {};
    t[typeKey][tierKey] = Array.isArray(t[typeKey][tierKey]) ? [...t[typeKey][tierKey]] : [0, 0];
    t[typeKey][tierKey][idx] = parseInt(val || "0", 10) || 0;
    setDraft({ ...draft, prices_tiers: t });
  };

  const tierBlock = (typeKey, label) => {
    const block = (tiers[typeKey] || {});
    return (
      <div className="rounded-2xl border border-slate-200 p-4 mb-3">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3">{label}</div>
        {[
          ["inside_aachen", "Aachen Stadt"],
          ["surrounding", "Aachen Umgebung"],
          ["long", "Köln / Düsseldorf / Fernstrecke"],
        ].map(([k, n]) => {
          const arr = block[k] || [0, 0];
          return (
            <div key={k} className="grid grid-cols-3 gap-2 mb-2 items-center">
              <div className="text-sm font-semibold text-slate-700">{n}</div>
              <input type="number" className={fieldClass} value={arr[0] ?? 0} onChange={(e) => setTier(typeKey, k, 0, e.target.value)} placeholder="min" />
              <input type="number" className={fieldClass} value={arr[1] ?? 0} onChange={(e) => setTier(typeKey, k, 1, e.target.value)} placeholder="max" />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div>
      <Card title="Preis-Tiers (Algorithmus)">
        <p className="text-xs text-slate-500 mb-3">Diese Werte werden vom System für die geschätzte Preisspanne im Angebot verwendet.</p>
        {tierBlock("small", "Kleine Transporte")}
        {tierBlock("full", "Kompletter Umzug")}
      </Card>

      <Card title="Preis-Karten (Startseite)" action={
        <button onClick={addCard} className="inline-flex items-center gap-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 px-3 py-1.5 text-xs font-semibold" data-testid="cms-add-price"><Plus size={12} /> Hinzufügen</button>
      }>
        {prices.map((p, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 p-4 mb-3" data-testid={`cms-price-${i}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
              <FieldRow label="Tag (z. B. Stadt)"><input className={fieldClass} value={p.tag || ""} onChange={(e) => updateCard(i, { tag: e.target.value })} /></FieldRow>
              <FieldRow label="Titel"><input className={fieldClass} value={p.title || ""} onChange={(e) => updateCard(i, { title: e.target.value })} /></FieldRow>
            </div>
            <FieldRow label="Preisspanne (Text)"><input className={fieldClass} value={p.range || ""} onChange={(e) => updateCard(i, { range: e.target.value })} placeholder="60€ – 100€" /></FieldRow>
            <FieldRow label="Beschreibung"><textarea rows={2} className={fieldClass} value={p.text || ""} onChange={(e) => updateCard(i, { text: e.target.value })} /></FieldRow>
            <div className="flex items-center justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!p.highlight} onChange={(e) => updateCard(i, { highlight: e.target.checked })} /> Hervorgehoben
              </label>
              <button onClick={() => removeCard(i)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 size={12} /> Löschen</button>
            </div>
          </div>
        ))}
      </Card>

      <Card title="Preis-Hinweis">
        <FieldRow label="Hinweis-Text">
          <textarea rows={2} className={fieldClass} value={draft.prices_note || ""} onChange={(e) => setDraft({ ...draft, prices_note: e.target.value })} />
        </FieldRow>
      </Card>

      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

function BewertungenTab({ draft, setDraft, onSave, saving }) {
  const reviews = draft.reviews || [];

  const update = (i, patch) => setDraft({ ...draft, reviews: reviews.map((r, idx) => (idx === i ? { ...r, ...patch } : r)) });
  const remove = (i) => setDraft({ ...draft, reviews: reviews.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, reviews: [...reviews, { name: "", city: "", rating: 5, text: "", avatar: "" }] });

  const onUpload = async (i, file) => {
    try {
      const url = await uploadImage(file);
      update(i, { avatar: url });
      toast.success("Foto hochgeladen");
    } catch {
      toast.error("Upload fehlgeschlagen");
    }
  };

  return (
    <div>
      <Card title="Kundenstimmen" action={
        <button onClick={add} className="inline-flex items-center gap-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 px-3 py-1.5 text-xs font-semibold" data-testid="cms-add-review"><Plus size={12} /> Hinzufügen</button>
      }>
        {reviews.map((r, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 p-4 mb-3" data-testid={`cms-review-${i}`}>
            <div className="flex items-start gap-3">
              <div className="shrink-0">
                <div className="w-14 h-14 rounded-full bg-slate-100 border-2 border-amber-100 overflow-hidden mb-1">
                  {r.avatar ? <img src={r.avatar} alt="" className="w-full h-full object-cover" /> : null}
                </div>
                <label className="inline-flex items-center gap-1 cursor-pointer text-[11px] font-semibold text-amber-900 hover:underline">
                  <Upload size={11} /> Foto
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUpload(i, e.target.files[0])} />
                </label>
              </div>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                <FieldRow label="Name"><input className={fieldClass} value={r.name || ""} onChange={(e) => update(i, { name: e.target.value })} /></FieldRow>
                <FieldRow label="Ort"><input className={fieldClass} value={r.city || ""} onChange={(e) => update(i, { city: e.target.value })} /></FieldRow>
                <FieldRow label="Sterne (1-5)"><input type="number" min={1} max={5} className={fieldClass} value={r.rating || 5} onChange={(e) => update(i, { rating: Math.max(1, Math.min(5, parseInt(e.target.value || "5", 10))) })} /></FieldRow>
                <FieldRow label="Avatar URL"><input className={fieldClass} value={r.avatar || ""} onChange={(e) => update(i, { avatar: e.target.value })} placeholder="https://..." /></FieldRow>
              </div>
            </div>
            <FieldRow label="Bewertungstext"><textarea rows={3} className={fieldClass} value={r.text || ""} onChange={(e) => update(i, { text: e.target.value })} /></FieldRow>
            <div className="flex justify-end">
              <button onClick={() => remove(i)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 size={12} /> Löschen</button>
            </div>
          </div>
        ))}
      </Card>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

function FAQTab({ draft, setDraft, onSave, saving }) {
  const faqs = draft.faqs || [];
  const update = (i, patch) => setDraft({ ...draft, faqs: faqs.map((f, idx) => (idx === i ? { ...f, ...patch } : f)) });
  const remove = (i) => setDraft({ ...draft, faqs: faqs.filter((_, idx) => idx !== i) });
  const add = () => setDraft({ ...draft, faqs: [...faqs, { q: "", a: "" }] });

  return (
    <div>
      <Card title="FAQ" action={
        <button onClick={add} className="inline-flex items-center gap-1 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-900 px-3 py-1.5 text-xs font-semibold" data-testid="cms-add-faq"><Plus size={12} /> Hinzufügen</button>
      }>
        {faqs.map((f, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 p-4 mb-3" data-testid={`cms-faq-${i}`}>
            <FieldRow label="Frage"><input className={fieldClass} value={f.q || ""} onChange={(e) => update(i, { q: e.target.value })} /></FieldRow>
            <FieldRow label="Antwort"><textarea rows={3} className={fieldClass} value={f.a || ""} onChange={(e) => update(i, { a: e.target.value })} /></FieldRow>
            <div className="flex justify-end">
              <button onClick={() => remove(i)} className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline"><Trash2 size={12} /> Löschen</button>
            </div>
          </div>
        ))}
      </Card>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

function RechtlichesTab({ draft, setDraft, onSave, saving }) {
  const legal = draft.legal || {};
  const imp = legal.impressum || {};
  const ds = legal.datenschutz || {};

  const setImp = (patch) => setDraft({ ...draft, legal: { ...legal, impressum: { ...imp, ...patch } } });
  const setDs = (patch) => setDraft({ ...draft, legal: { ...legal, datenschutz: { ...ds, ...patch } } });

  return (
    <div>
      <Card title="Impressum / Firmenangaben">
        <FieldRow label="Inhaber (vollständiger Name)"><input className={fieldClass} value={imp.owner || ""} onChange={(e) => setImp({ owner: e.target.value })} data-testid="cms-imp-owner" /></FieldRow>
        <FieldRow label="Verantwortlich für Inhalt"><input className={fieldClass} value={imp.responsible || ""} onChange={(e) => setImp({ responsible: e.target.value })} /></FieldRow>
        <FieldRow label="Adresse (Straße, PLZ, Ort)"><textarea rows={2} className={fieldClass} value={imp.address || ""} onChange={(e) => setImp({ address: e.target.value })} data-testid="cms-imp-address" /></FieldRow>
        <FieldRow label="Telefon"><input className={fieldClass} value={imp.phone || ""} onChange={(e) => setImp({ phone: e.target.value })} /></FieldRow>
        <FieldRow label="E-Mail"><input className={fieldClass} value={imp.email || ""} onChange={(e) => setImp({ email: e.target.value })} data-testid="cms-imp-email" /></FieldRow>
        <FieldRow label="USt-ID (falls vorhanden)"><input className={fieldClass} value={imp.ust_id || ""} onChange={(e) => setImp({ ust_id: e.target.value })} /></FieldRow>
      </Card>
      <Card title="Datenschutz – Zusatztexte (optional)">
        <FieldRow label="Einleitung (optional)" hint="Ersetzt den Standardtext, falls gesetzt.">
          <textarea rows={3} className={fieldClass} value={ds.intro || ""} onChange={(e) => setDs({ intro: e.target.value })} />
        </FieldRow>
        <FieldRow label="Hosting-Hinweis (optional)">
          <textarea rows={3} className={fieldClass} value={ds.hosting || ""} onChange={(e) => setDs({ hosting: e.target.value })} />
        </FieldRow>
      </Card>
      <SaveBar onSave={onSave} saving={saving} />
    </div>
  );
}

function EinstellungenTab() {
  const [currentPwd, setCurrentPwd] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [newPwd2, setNewPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
  }, []);

  const onSubmit = async () => {
    if (!currentPwd) {
      toast.error("Bitte aktuelles Passwort eingeben");
      return;
    }
    if (newPwd && newPwd !== newPwd2) {
      toast.error("Neue Passwörter stimmen nicht überein");
      return;
    }
    if (!newPwd && !newUsername) {
      toast.error("Bitte Benutzername oder Passwort ändern");
      return;
    }
    setLoading(true);
    try {
      const body = { current_password: currentPwd };
      if (newUsername) body.new_username = newUsername;
      if (newPwd) body.new_password = newPwd;
      const { data } = await api.patch("/admin/settings/credentials", body);
      if (data?.token) {
        localStorage.setItem("admin_token", data.token);
        if (data.user) localStorage.setItem("admin_user", JSON.stringify(data.user));
      }
      toast.success("Zugangsdaten aktualisiert");
      setCurrentPwd(""); setNewUsername(""); setNewPwd(""); setNewPwd2("");
      const refreshed = await api.get("/auth/me");
      setMe(refreshed.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Fehler beim Speichern";
      toast.error(typeof msg === "string" ? msg : "Fehler");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Card title="Admin-Konto">
        <div className="text-sm text-slate-600 mb-4">
          Aktueller Benutzer: <span className="font-semibold text-slate-900">{me?.username || "—"}</span>
        </div>
        {me && me.password_changed === false && (
          <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3 mb-4 text-sm text-amber-900 flex items-start gap-2" data-testid="default-pwd-warning">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>Bitte ändern Sie Ihr Admin-Passwort vor Veröffentlichung.</span>
          </div>
        )}
        <FieldRow label="Aktuelles Passwort"><input type="password" className={fieldClass} value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} autoComplete="current-password" data-testid="cms-current-pwd" /></FieldRow>
        <FieldRow label="Neuer Benutzername (optional)"><input className={fieldClass} value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder={me?.username || ""} data-testid="cms-new-username" /></FieldRow>
        <FieldRow label="Neues Passwort (optional, min. 6 Zeichen)"><input type="password" className={fieldClass} value={newPwd} onChange={(e) => setNewPwd(e.target.value)} autoComplete="new-password" data-testid="cms-new-pwd" /></FieldRow>
        <FieldRow label="Neues Passwort wiederholen"><input type="password" className={fieldClass} value={newPwd2} onChange={(e) => setNewPwd2(e.target.value)} autoComplete="new-password" data-testid="cms-new-pwd-confirm" /></FieldRow>
        <div className="flex justify-end">
          <button onClick={onSubmit} disabled={loading} className="inline-flex items-center gap-2 rounded-2xl bg-brand-navy text-white px-5 py-3 text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 transition-colors" data-testid="cms-save-credentials">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Aktualisieren
          </button>
        </div>
      </Card>
    </div>
  );
}
